const DEFAULT_CITY_KEY = 'us_ca_los_angeles';
const MAX_REGION_LENGTH = 10;
const CANONICAL_CITY_KEY_PATTERN = /^[a-z]{2}_[a-z0-9]{1,10}_[a-z0-9_]+$/;

function sanitizeSegment(value?: string | null): string {
  if (!value) return '';
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function toUnderscore(value: string): string {
  return value.replace(/-/g, '_');
}

function toHyphen(value: string): string {
  return value.replace(/_/g, '-');
}

export function cityKeyFromLocation(
  location?: {
    locationCountry?: string | null;
    locationRegion?: string | null;
    locationCity?: string | null;
  } | null,
  fallback = DEFAULT_CITY_KEY,
): string {
  const city = sanitizeSegment(location?.locationCity);
  if (!city) return fallback;

  const countryRaw = sanitizeSegment(location?.locationCountry);
  const country = (countryRaw.slice(0, 2) || 'us').toLowerCase();
  const region = sanitizeSegment(location?.locationRegion).slice(0, MAX_REGION_LENGTH);

  return region ? `${country}_${region}_${city}` : `${country}_${city}`;
}

export function normalizeCityKeyForStorage(
  raw?: string | null,
  fallback = DEFAULT_CITY_KEY,
): string {
  const normalized = sanitizeSegment(raw);
  if (!normalized) return fallback;
  return toUnderscore(normalized);
}

export function cityKeyCandidates(raw?: string | null): string[] {
  const normalized = sanitizeSegment(raw);
  if (!normalized) return [];

  const set = new Set<string>();
  const underscored = toUnderscore(normalized);
  const hyphenated = toHyphen(underscored);

  set.add(underscored);
  set.add(hyphenated);

  const parts = underscored.split('_').filter(Boolean);

  if (CANONICAL_CITY_KEY_PATTERN.test(underscored)) {
    const cityPart = parts.slice(2).join('_');
    if (cityPart) {
      set.add(cityPart);
      set.add(toHyphen(cityPart));
    }
  } else if (parts.length >= 2 && parts[0].length === 2) {
    const cityPart = parts.slice(1).join('_');
    if (cityPart) {
      set.add(cityPart);
      set.add(toHyphen(cityPart));
    }
  }

  return Array.from(set).filter(Boolean);
}

// Backward-compatible helper used by existing FE code
export function toCityKey(country?: string, region?: string, city?: string): string {
  return cityKeyFromLocation({
    locationCountry: country ?? null,
    locationRegion: region ?? null,
    locationCity: city ?? null,
  });
}
